import { Component, OnInit } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { EServiceCategoryRequest } from './e-service-category.model';
import { EServiceCategoryService } from './e-service-category.service';

@Component({
  selector: 'app-e-service-category',
  standalone: false,
  templateUrl: './e-service-category.component.html',
  styleUrls: ['./e-service-category.component.css']
})
export class EServiceCategoryComponent implements OnInit {

  successMessage = '';
  errorMessage = '';

  languages: any[] = [];
  categories: any[] = [];
  filteredCategories: any[] = [];

  currentPage = 1;
  pageSize = 5;

  activeTab = 'all';
  activeTabPopup = 1;
  searchText = '';

  isModalOpen = false;
  isEnableSave = false;
  isEnableUpdate = false;
  isEnableDelete = false;

  categoryId = 0;
  icon = '';
  isFront = false;
  isEnabled = true;

  nameKh = '';
  descriptionKh = '';
  nameEn = '';
  descriptionEn = '';

  constructor(
    private categoryService: EServiceCategoryService,
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    this.loadLanguages();
    this.loadCategories('');
  }

  loadLanguages(): void {
    this.languageService.getAll().subscribe({
      next: (res: any) => {
        this.languages = (res.data || []).sort((a: any, b: any) => a.languageId - b.languageId);

        if (this.languages.length) {
          this.setTab(this.languages[0].code);
          this.setTabPopup(this.languages[0].languageId);
        }
      },
      error: () => this.showError('Failed to load languages.')
    });
  }

  loadCategories(langCode: string): void {
    this.categoryService.getAll(langCode).subscribe({
      next: (res: any) => {
        const apiData = res.data || [];

        this.categories = apiData
          .filter((item: any) => item.translations?.length)
          .flatMap((item: any) =>
            item.translations.map((t: any) => ({
              categoryId: item.id,
              icon: item.icon,
              isFront: item.isFront,
              isEnabled: item.isEnabled,
              languageCode: t.languageCode,
              name: t.name,
              description: t.description
            }))
          );

        this.filterCategories(this.activeTab);
      },
      error: () => this.showError('Failed to load e-service categories.')
    });
  }

  setTab(tab: string): void {
    this.activeTab = tab;
    this.currentPage = 1;
    this.filterCategories(tab);
  }

  setTabPopup(languageId: number): void {
    this.activeTabPopup = languageId;
  }

  filterCategories(tab: string): void {
    const keyword = this.searchText.trim().toLowerCase();
    this.currentPage = 1;

    this.filteredCategories = this.categories.filter(category => {
      const frontStatus = category.isFront ? 'Shown' : 'Hidden';
      const status = category.isEnabled ? 'Active' : 'Inactive';
      const matchesTab = tab === 'all' || category.languageCode === tab;
      const matchesSearch = !keyword ||
        `${category.name ?? ''} ${category.description ?? ''} ${category.icon ?? ''} ${frontStatus} ${status} ${category.languageCode ?? ''}`
          .toLowerCase()
          .includes(keyword);

      return matchesTab && matchesSearch;
    });
  }

  getLanguageCount(code: string): number {
    return this.categories.filter(c => c.languageCode === code).length;
  }

  get pagedCategories(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredCategories.slice(start, start + this.pageSize);
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredCategories.length / this.pageSize));
  }

  changePage(page: number): void {
    this.currentPage = page;
  }

  addNew(): void {
    this.resetForm();
    this.openModal('Add');
  }

  viewData(id: number, mode: 'Edit' | 'Delete'): void {
    this.categoryService.getById(id, '').subscribe({
      next: (res: any) => {
        const data = res.data;

        if (!data) {
          this.showError('E-service category not found.');
          return;
        }

        this.categoryId = data.id;
        this.icon = data.icon || '';
        this.isFront = data.isFront;
        this.isEnabled = data.isEnabled;

        this.nameKh = '';
        this.descriptionKh = '';
        this.nameEn = '';
        this.descriptionEn = '';

        data.translations.forEach((t: any) => {
          if (t.languageCode === 'kh') {
            this.nameKh = t.name;
            this.descriptionKh = t.description || '';
          }

          if (t.languageCode === 'en') {
            this.nameEn = t.name;
            this.descriptionEn = t.description || '';
          }
        });

        this.activeTabPopup = this.activeTab === 'kh' ? 1 : 2;
        this.openModal(mode);
      },
      error: () => this.showError('Unable to load e-service category.')
    });
  }

  save(): void {
    if (!this.validateForm()) {
      return;
    }

    this.categoryService.create(this.buildPayload()).subscribe({
      next: () => {
        this.showSuccess('E-service category created successfully.');
        this.afterSuccess();
      },
      error: () => this.showError('Failed to create e-service category.')
    });
  }

  update(): void {
    if (!this.validateForm()) {
      return;
    }

    this.categoryService.update(this.categoryId, this.buildPayload()).subscribe({
      next: () => {
        this.showSuccess('E-service category updated successfully.');
        this.afterSuccess();
      },
      error: () => this.showError('Failed to update e-service category.')
    });
  }

  delete(): void {
    if (!this.categoryId) {
      this.showError('Please select an e-service category.');
      return;
    }

    if (!confirm('Are you sure you want to delete this e-service category?')) {
      return;
    }

    this.categoryService.delete(this.categoryId).subscribe({
      next: () => {
        this.showSuccess('E-service category deleted successfully.');
        this.afterSuccess();
      },
      error: () => this.showError('Failed to delete e-service category.')
    });
  }

  openModal(eventName?: string): void {
    this.visibleButtonControl(eventName || '');
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  visibleButtonControl(eventName: string): void {
    this.isEnableSave = eventName === 'Add';
    this.isEnableUpdate = eventName === 'Edit';
    this.isEnableDelete = eventName === 'Delete';
  }

  private buildPayload(): EServiceCategoryRequest {
    return {
      icon: this.icon,
      isFront: this.isFront,
      isEnabled: this.isEnabled,
      translations: [
        {
          languageId: 1,
          name: this.nameKh,
          description: this.descriptionKh
        },
        {
          languageId: 2,
          name: this.nameEn,
          description: this.descriptionEn
        }
      ]
    };
  }

  private validateForm(): boolean {
    if (!this.nameKh.trim() || !this.nameEn.trim()) {
      this.showError('Please enter category names for both languages.');
      return false;
    }

    return true;
  }

  private afterSuccess(): void {
    this.closeModal();
    this.resetForm();
    this.loadCategories('');
  }

  private resetForm(): void {
    this.categoryId = 0;
    this.icon = '';
    this.isFront = false;
    this.isEnabled = true;
    this.nameKh = '';
    this.descriptionKh = '';
    this.nameEn = '';
    this.descriptionEn = '';
    this.activeTabPopup = this.languages[0]?.languageId || 1;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => this.clearMessages(), 3000);
  }

  private showError(message: string): void {
    this.errorMessage = message;
    setTimeout(() => this.clearMessages(), 3000);
  }

  private clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

}
